/**
 * Theme controller (F-23, AD-01, THEME-MODE-INVARIANT).
 *
 * Three modes: `system`, `light`, `dark`. **`dark` is the DEFAULT** — owner decision 2026-09-08,
 * given after they went through the mockups. `system` is still a mode the user can choose, and once
 * chosen it FOLLOWS the operating system preference live, including a change while the app is open.
 * An explicit choice is saved and wins until the user clears it. No entry page forces light.
 *
 * This deliberately departs from `redesign/proposals/astra/tokens.json .contracts.theme`, which
 * states the default is `system`. That file is FROZEN recovery evidence (D-01) and is NOT edited;
 * the owner's later decision supersedes it, and this comment is where the departure is recorded.
 *
 * What this controller must never do: change layout, wording, section order, data or interactions.
 * It writes exactly two attributes on <html> — `data-theme` and `color-scheme` — and nothing else.
 * Palette, contrast and artwork lighting follow from those. Responsive reflow follows viewport
 * width and is not this controller's business.
 *
 * AD-01 boundary: a remembered theme is a display preference. It carries NO authority. This module
 * deliberately holds no session, actor or permission state, and nothing here may read any.
 */
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { hasReceipt, persistWithReceipt } from './receipts.js';

const STORAGE_KEY = 'chorestory.theme';
/** The mode used when the user has made no choice at all. Owner decision 2026-09-08. */
const DEFAULT_MODE = 'dark';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const ThemeContext = createContext(null);

function readSavedMode() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
    return DEFAULT_MODE;
  } catch {
    // Private-mode storage denial is not a failure worth surfacing; fall back to the default.
    return DEFAULT_MODE;
  }
}

function systemPrefersDark() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

export function ThemeController({ children, profileId, preferenceStore }) {
  const [binding, setBinding] = useState(null);
  const configureProfile = useCallback((next) => setBinding(next), []);
  const selectedId = profileId !== undefined ? profileId : binding?.profileId;
  const store = preferenceStore || binding?.store;
  const storageKey = selectedId ? `${STORAGE_KEY}.profile:${selectedId}` : STORAGE_KEY;
  const [choices, setChoices] = useState(() => ({ [STORAGE_KEY]: { mode: readSavedMode(), persistence: 'device' } }));
  const deviceMode = choices[STORAGE_KEY]?.mode || readSavedMode();
  const mode = choices[storageKey]?.mode || (selectedId && typeof store?.readTheme === 'function' ? DEFAULT_MODE : deviceMode);
  const persistence = choices[storageKey]?.persistence || (selectedId ? 'unconnected' : 'device');
  const generation = useRef(0);
  const writes = useRef(new Map());
  const currentKey = useRef(storageKey);
  currentKey.current = storageKey;
  const put = (key, choice) => setChoices(previous => ({ ...previous, [key]: choice }));
  // A LAYOUT effect so the read is counted before any click can be: a passive one ran after a choice
  // made in the same frame, and its answer overwrote that choice (d-20261001-04 kept Settings mounted).
  useLayoutEffect(() => {
    const revision = ++generation.current;
    if (!selectedId || typeof store?.readTheme !== 'function') return undefined;
    put(storageKey, { mode: choices[storageKey]?.mode || DEFAULT_MODE, persistence: 'loading' });
    Promise.resolve().then(() => store.readTheme(selectedId)).then(receipt => {
      if (generation.current !== revision || currentKey.current !== storageKey) return;
      if (!hasReceipt(receipt, 'loaded') || receipt.profileId !== selectedId || !['system', 'light', 'dark'].includes(receipt.mode)) throw new Error('No profile preference receipt');
      put(storageKey, { mode: receipt.mode, persistence: 'saved' });
    }).catch(() => {
      if (generation.current === revision && currentKey.current === storageKey)
        put(storageKey, { mode: DEFAULT_MODE, persistence: 'error' });
    });
    // Bumping the LIVE counter at cleanup is the point: it invalidates a read still in flight.
    // Copying it to a local, as the rule suggests, would invalidate nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { ++generation.current; };
    // `choices` is read only for the mode to show while loading, and this effect WRITES `choices`
    // through `put`. Listing it would re-issue the profile read after every write it makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, store, storageKey]);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
    document.documentElement.style.colorScheme = resolved;
  }, [resolved]);

  const setMode = useCallback((next) => {
    if (!['light', 'dark', 'system'].includes(next)) throw new Error(`unknown theme mode: ${String(next)}`);
    const revision = ++generation.current;
    // Apply an in-memory preview immediately. Profile success requires host persistence.
    put(storageKey, { mode: next, persistence: selectedId ? 'saving' : 'device' });
    if (!selectedId) {
      try {
        if (next === 'system') window.localStorage.removeItem(STORAGE_KEY);
        else window.localStorage.setItem(STORAGE_KEY, next);
      } catch { /* The current display choice still works with storage denied. */ }
      return;
    }
    const operation = (writes.current.get(storageKey) || Promise.resolve()).then(() =>
      persistWithReceipt(store?.saveTheme?.bind(store), [selectedId, next], 'saved',
        r => r.profileId === selectedId && r.mode === next));
    writes.current.set(storageKey, operation.catch(() => {}));
    return operation.then(receipt => {
        if (revision !== generation.current || currentKey.current !== storageKey) return;
        put(storageKey, { mode: next, persistence: receipt ? 'saved' : 'unconnected' });
      }).catch(() => {
        if (revision === generation.current && currentKey.current === storageKey)
          put(storageKey, { mode: next, persistence: 'error' });
      });
  }, [selectedId, store, storageKey]);

  const value = useMemo(() => ({ mode, resolved, setMode, persistence, configureProfile }),
    [mode, resolved, setMode, persistence, configureProfile]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeController>');
  return value;
}

/** Optional for standalone surfaces; absence never creates a profile or an identity. */
export function useOptionalTheme() { return useContext(ThemeContext); }
